import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ApiError, type DeliveryMethod } from '@chicare/shared';

import { api } from '@/lib/api';
import { cartStore, useCartLines, useCheckoutKey, usePricedCart } from '@/lib/cart';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { AddressStep } from './AddressStep';

/**
 * Checkout.
 *
 * The one screen where §11's copy rules are load-bearing rather than stylistic.
 * A buyer must understand, before they tap, that the delivery fee is charged
 * now and the medicines may not be — because the whole trust model depends on
 * them expecting to check the pack at the door and pay after.
 *
 * The charging promise is not written here. It comes from the API, generated by
 * the same policy that will run the charge, so the screen cannot drift from the
 * behaviour.
 */
export function CheckoutScreen() {
  const navigate = useNavigate();
  const lines = useCartLines();
  const { user } = useSession();
  const { key: idempotencyKey, reset: resetKey } = useCheckoutKey();

  const [method, setMethod] = useState<DeliveryMethod>('standard');
  const [addressId, setAddressId] = useState<number | null>(null);
  const [placed, setPlaced] = useState(false);
  const [useCredit, setUseCredit] = useState(true);

  // The server clamps credit to min(requested, held, order total), so asking
  // for "all of it" is a sentinel rather than a number the client has to know.
  const APPLY_ALL_CREDIT = 999_999_999;

  const { data: cart, isLoading } = usePricedCart({
    deliveryMethod: method,
    creditKobo: useCredit ? APPLY_ALL_CREDIT : 0,
  });

  const place = useMutation({
    mutationFn: () =>
      api.placeOrder({
        items: lines,
        addressId,
        deliveryMethod: method,
        creditKobo: useCredit ? APPLY_ALL_CREDIT : 0,
        // ONE key for this whole attempt. Every retry reuses it, which is the
        // difference between a retry and a second order (§9.14).
        idempotencyKey,
      }),
    onSuccess: (order) => {
      // Order matters. Clearing the cart re-renders this screen with an empty
      // cart, and the empty-cart guard below would otherwise bounce the buyer
      // to /cart — losing the order confirmation they just earned. `placed`
      // closes that race before the cart is touched.
      setPlaced(true);
      cartStore.clear();
      resetKey();
      navigate(`/orders/${order.reference}`, { replace: true, state: { justPlaced: true } });
    },
  });

  const error = place.error instanceof ApiError ? place.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  // <Navigate>, not a navigate() call in the render body: redirecting as a side
  // effect of rendering is how the success redirect got overwritten in the
  // first place.
  if (lines.length === 0 && !placed) {
    return <Navigate to="/cart" replace />;
  }

  return (
    <>
      <AppBar back title="Checkout" />

      <div className="mx-auto w-full max-w-[900px] px-4 pb-6 xl:px-6">
        {isLoading || !cart ? (
          <div className="space-y-3 pt-3.5">
            <Skeleton className="h-32 w-full" radius="card" />
            <Skeleton className="h-48 w-full" radius="card" />
          </div>
        ) : (
          <div className="space-y-3 pt-3.5">
            {/* 1 — Where it goes ------------------------------------------ */}
            {method !== 'pickup' ? (
              <Card>
                <StepHeading number={1} title="Where should it go?" />
                <div className="mt-3">
                  <AddressStep
                    selectedId={addressId}
                    onSelect={setAddressId}
                    error={fieldErrors.address_id?.[0]}
                  />
                </div>
              </Card>
            ) : null}

            {/* 2 — Delivery ------------------------------------------------ */}
            <Card>
              <StepHeading number={method !== 'pickup' ? 2 : 1} title="How should it arrive?" />

              <div className="mt-3 space-y-2">
                {(
                  [
                    { key: 'standard', label: 'Standard', eta: 'Today by 5pm' },
                    { key: 'express', label: 'Express', eta: 'Within 2 hours' },
                    { key: 'pickup', label: 'Pick up at the pharmacy', eta: 'Ready in 40 mins' },
                  ] as const
                ).map((option) => {
                  const selected = method === option.key;
                  const fee =
                    option.key === 'pickup'
                      ? 'Free'
                      : selected
                        ? cart.delivery_total_display
                        : null;

                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setMethod(option.key)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-[9px] border px-3 py-3 text-left',
                        selected ? 'border-[1.5px] border-blue bg-blue-tint-2' : 'border-line',
                      )}
                    >
                      <span
                        className={cn(
                          'grid h-[17px] w-[17px] shrink-0 place-items-center rounded border-[1.5px]',
                          selected ? 'border-blue bg-blue' : 'border-line',
                        )}
                      >
                        {selected ? (
                          <Icon name="check" size={10} strokeWidth={3.4} className="text-white" />
                        ) : null}
                      </span>

                      <span className="min-w-0 flex-1">
                        <b className="block text-[12.5px]">{option.label}</b>
                        <span className="text-[11px] text-faint">{option.eta}</span>
                      </span>

                      {fee ? (
                        <span
                          className={cn(
                            'shrink-0 font-mono text-[12px] tabular',
                            option.key === 'pickup' && 'font-bold text-verified',
                          )}
                        >
                          {fee}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

            </Card>

            {/* 3 — Refund credit ------------------------------------------- */}
            {cart.credit_available_kobo > 0 ? (
              <Card>
                <label className="flex cursor-pointer items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={useCredit}
                    onClick={() => setUseCredit((on) => !on)}
                    className={cn(
                      'relative h-[23px] w-10 shrink-0 rounded-pill transition-colors',
                      useCredit ? 'bg-blue' : 'bg-line',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-[2.5px] h-[18px] w-[18px] rounded-full bg-white transition-all',
                        useCredit ? 'left-[19.5px]' : 'left-[2.5px]',
                      )}
                    />
                  </button>
                  <span className="min-w-0 flex-1">
                    <b className="block text-[12.5px]">Use refund credit</b>
                    <span className="text-[11px] text-faint">
                      {cart.credit_available_display} available. Spendable on ChiCare, never
                      withdrawable.
                    </span>
                  </span>
                </label>
              </Card>
            ) : null}

            {/* 4 — What gets charged, and when ----------------------------- */}
            <Card>
              <StepHeading
                number={
                  (method !== 'pickup' ? 1 : 0) + (cart.credit_available_kobo > 0 ? 1 : 0) + 2
                }
                title="What you pay"
              />

              <div className="mt-3 space-y-1">
                <Row label="Medicines" value={cart.goods_total_display} />
                <Row
                  label={`Delivery · ${cart.delivery_count} ${cart.delivery_count === 1 ? 'trip' : 'trips'}`}
                  value={cart.delivery_total_display}
                />
                {cart.credit_applied_kobo > 0 ? (
                  <Row
                    label="Refund credit"
                    value={`−${cart.credit_applied_display}`}
                    tone="verified"
                  />
                ) : null}
              </div>

              <div className="my-3 h-px bg-line-2" />

              <div className="flex items-center justify-between">
                <b className="text-[14px]">Total</b>
                <span className="font-display text-[20px] font-bold tracking-[-0.02em]">
                  {cart.grand_total_display}
                </span>
              </div>
            </Card>

            {/* The promise, per pharmacy. §11 requires this said plainly. */}
            <div className="space-y-2">
              {cart.groups.map((group) => (
                <div
                  key={group.pharmacy?.id}
                  className="rounded-card border border-blue-tint bg-blue-tint-2 px-3.5 py-3"
                >
                  <div className="flex items-center gap-2">
                    {/* A shop, not a shield. This heads the group of items
                        coming from one pharmacy; it never meant "verified". */}
                    <Icon name="store" size={14} strokeWidth={1.9} className="shrink-0 text-blue" />
                    <b className="text-[12px] text-blue-deep">{group.pharmacy?.name}</b>
                  </div>
                  {/* ── NO TIMING CLAIM FOR THE GOODS ───────────────────────
                      This promised "charged at your door, after you check the
                      pack" to everybody, and promised the collect-equivalent
                      for pickup. Neither is reliably true: CapturePolicy picks
                      checkout, dispatch or door, DISPATCH is the default, and
                      POM, cold chain, a missing card and every new buyer are
                      all forced to checkout before the tier table is read.

                      The honest version cannot be written from here, because
                      no sub-order exists yet and the trigger is evaluated at
                      execution time (§9.19). So this states only what is
                      certain — the delivery fee goes now — and makes no claim
                      it cannot keep.

                      There IS machinery for the correct sentence:
                      `captureExplanation` in shared/domain.ts, and
                      SubOrderResource already ships trigger, label and
                      explanation. Nothing calls either. Wiring a PREDICTED
                      trigger into the cart payload would let this screen tell
                      the truth per pharmacy rather than stay silent. */}
                  <p className="m-0 mt-1 text-[11.5px] leading-relaxed text-blue-deep">
                    Delivery {group.delivery_fee_display} — charged now, covers the rider&apos;s
                    trip. Medicines {group.goods_display} — on the same card.
                  </p>
                </div>
              ))}
            </div>

            {/* §4.5 requires this readable AT checkout, not buried. */}
            <p className="px-1 text-[11px] leading-relaxed text-faint">
              Medicines can&apos;t be returned once you&apos;ve accepted them. Check your pack with
              the rider — that&apos;s what the seal and batch check are for. If something is wrong
              with what you received, we&apos;ll refund you.{' '}
              <a href="/pages/returns-policy" className="font-medium text-blue">
                Read the full policy
              </a>
            </p>

            {error && !error.isValidation ? (
              <p role="alert" className="rounded-lg bg-danger-tint px-3 py-2.5 text-[12px] font-medium text-danger">
                {error.message}
              </p>
            ) : null}

            {fieldErrors.items ? (
              <p role="alert" className="rounded-lg bg-danger-tint px-3 py-2.5 text-[12px] font-medium text-danger">
                {fieldErrors.items[0]}
              </p>
            ) : null}
          </div>
        )}
      </div>

      {cart ? (
        <div className="sticky bottom-0 border-t border-line bg-white px-4 pb-3.5 pt-3 shadow-[0_-4px_16px_rgba(8,19,43,0.06)]">
          <div className="mx-auto max-w-[900px]">
            <div className="mb-2.5 flex items-center justify-between">
              <span className="text-[11px] text-faint">
                {user?.name ? `Ordering as ${user.name}` : 'Total to pay now'}
              </span>
              <span className="font-display text-[20px] font-bold tracking-[-0.02em]">
                {cart.grand_total_display}
              </span>
            </div>
            <Button
              full
              loading={place.isPending}
              disabled={method !== 'pickup' && addressId === null}
              onClick={() => place.mutate()}
            >
              Place order
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function StepHeading({ number, title }: { number: number; title: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-blue text-[11px] font-bold text-white">
        {number}
      </span>
      <b className="text-[13px]">{title}</b>
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'verified' }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11.5px] text-muted">{label}</span>
      <span
        className={cn('font-mono text-[12.5px] tabular', tone === 'verified' && 'font-bold text-verified')}
      >
        {value}
      </span>
    </div>
  );
}
